import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SeverityBadge } from "@/components/SeverityBadge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkle, ShieldWarning, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

function Field({ label, value, mono = true }) {
  return (
    <div className="py-2.5 border-b border-[#1F1F33]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
      <div className={`text-sm text-white break-words ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

export default function ThreatDrawer({ threat, open, onOpenChange, onAnalyzed, onCreateIncident }) {
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState("");

  useEffect(() => {
    setAnalysis(threat?.ai_analysis || "");
  }, [threat]);

  if (!threat) return null;

  const runAnalysis = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/threats/analyze", { threat_id: threat.id });
      setAnalysis(data.analysis);
      onAnalyzed && onAnalyzed(threat.id, data.analysis);
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#0C0C14] border-l border-[#1F1F33] text-white w-full sm:max-w-md overflow-y-auto font-mono p-0"
        data-testid="threat-drawer"
      >
        <div className="p-6">
          <SheetTitle className="sr-only">Threat Detail: {threat.title}</SheetTitle>
          <div className="flex items-center gap-2 text-[#00F0FF] mb-3">
            <ShieldWarning size={18} weight="duotone" />
            <span className="text-[10px] uppercase tracking-[0.25em]">Threat Detail</span>
          </div>
          <h2 className="font-head text-2xl tracking-tight font-bold mb-3">{threat.title}</h2>
          <SeverityBadge level={threat.severity} />

          <div className="mt-4">
            <Field label="Threat ID" value={threat.id} />
            <Field label="Category" value={threat.category} />
            <Field label="Status" value={(threat.status || "").toUpperCase()} />
            <Field label="Source IP" value={threat.source_ip} />
            <Field label="Target Asset" value={threat.target} />
            <Field label="Detected" value={new Date(threat.detected_at).toLocaleString()} />
            <Field label="Description" value={threat.description} mono={false} />
          </div>

          <div className="mt-5 bg-[#12121E] border border-[#00F0FF]/30 p-4" style={{ boxShadow: "0 0 30px -18px rgba(0,240,255,0.4)" }}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={14} weight="fill" color="#00F0FF" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">AI Analysis</span>
            </div>
            {analysis ? (
              <pre data-testid="drawer-ai-result" className="whitespace-pre-wrap text-xs leading-relaxed text-[#A1A1AA]">{analysis}</pre>
            ) : (
              <p className="text-xs text-[#71717A]">No analysis generated yet.</p>
            )}
            <button
              data-testid="drawer-run-analysis"
              onClick={runAnalysis}
              disabled={busy}
              className="w-full mt-3 flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] py-2.5 hover:bg-white active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
            >
              <Sparkle size={13} weight="fill" />
              {busy ? "Analyzing…" : analysis ? "Re-run" : "Run AI Analysis"}
            </button>
          </div>

          <button
            data-testid="drawer-create-incident"
            onClick={() => onCreateIncident && onCreateIncident(threat)}
            className="w-full mt-4 flex items-center justify-center gap-2 border border-[#FF9500]/50 text-[#FF9500] uppercase tracking-[0.15em] text-[11px] py-2.5 hover:bg-[#FF9500]/10 transition-colors"
          >
            <ArrowSquareOut size={14} /> Escalate to Incident
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
